import { Footer } from '@/components/Footer';
import { Header } from '@/components/Header';
import { Hero } from '@/components/Hero';
import { ProductCardSkeleton } from '@/components/ProductCardSkeleton';
import { ProductCategoryRow } from '@/components/ProductCategoryRow';
import { ProductDetailSheet } from '@/components/ProductDetailSheet';
import { ProductGrid } from '@/components/ProductGrid';
import { SearchCommand } from '@/components/SearchCommand';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  SegmentedControl,
  SegmentedControlItem,
} from '@/components/ui/segmented-control';
import { Skeleton } from '@/components/ui/skeleton';
import { useIntersectionObserver } from '@/hooks/useIntersectionObserver';
import { useBranchStore } from '@/store/branchStore';
import { fetchStockCounts, StockCounts } from '@/lib/api';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AlertCircleIcon, ArrowLeft01Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from "@hugeicons/react";

const Index = () => {
  const [activeTab, setActiveTab] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [stockCounts, setStockCounts] = useState<StockCounts | null>(null);

  const location = useLocation();
  const navigate = useNavigate();
  const isProductsRoute = location.pathname === '/products';

  const branchId = useBranchStore(s => s.branchId);
  const products = useBranchStore(s => s.products);
  const pagination = useBranchStore(s => s.pagination);
  const branchLoading = useBranchStore(s => s.branchLoading);
  const productsLoading = useBranchStore(s => s.productsLoading);
  const productsError = useBranchStore(s => s.productsError);
  const channelLoading = useBranchStore(s => s.channelLoading);
  const initialized = useBranchStore(s => s.initialized);
  const loadProducts = useBranchStore(s => s.loadProducts);

  useEffect(() => {
    if (branchId) {
      fetchStockCounts(branchId).then(setStockCounts).catch(console.error);
    }
  }, [branchId]);

  const [searchBarRef, isSearchBarVisible] = useIntersectionObserver({
    threshold: 0,
    rootMargin: '-1px 0px 0px 0px',
  });

  const isSectionView = activeTab === 'all' && !selectedCategory && !isProductsRoute;

  const isBranchMissing =
    typeof window !== 'undefined' &&
    initialized &&
    !branchId &&
    !channelLoading;

  const { frequentlyBought, prescriptionOnlyProducts, otcProducts, others } =
    useMemo(() => {
      const list = products ?? [];
      const frequentlyBought = list.filter(p => (p.sales ?? 0) > 5);

      const prescriptionOnlyProducts = list.filter(
        p => p.category?.toUpperCase() === 'POM'
      );

      const otcProducts = list.filter(p => p.category?.toUpperCase() === 'OTC');

      const others = list.filter(
        p =>
          !((p.sales ?? 0) > 5) &&
          p.category?.toUpperCase() !== 'POM' &&
          p.category?.toUpperCase() !== 'OTC'
      );

      return {
        frequentlyBought,
        prescriptionOnlyProducts,
        otcProducts,
        others,
      };
    }, [products]);

  const prescriptionCount = stockCounts?.pom ?? (pagination?.total ? otcProducts.length : prescriptionOnlyProducts.length);
  const otcCount = stockCounts?.otc ?? (pagination?.total ? otcProducts.length : otcProducts.length);
  const allCount = stockCounts?.total ?? (pagination?.total || products.length);
  const frequentlyBoughtCount = frequentlyBought.length;
  const othersCount = stockCounts?.others ?? others.length;

  const handleTabChange = useCallback((value: string) => {
    setActiveTab(value);
    setSelectedCategory(null);
  }, []);

  const handlePageChange = (newPage: number) => {
    if (!pagination || productsLoading) return;
    if (newPage < 1 || newPage > pagination.totalPages) return;

    loadProducts(newPage, pagination.limit, false);

    // Smooth scroll back to top of main area for context change feedback
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const activeProducts = useMemo(() => {
    if (activeTab === 'prescription') return prescriptionOnlyProducts;
    if (activeTab === 'otc') return otcProducts;
    return [];
  }, [activeTab, prescriptionOnlyProducts, otcProducts]);

  const productsForCategoryView = useMemo(() => {
    if (!selectedCategory) return [];
    if (selectedCategory === 'Frequently Bought') return frequentlyBought;
    if (selectedCategory === 'Prescription Only')
      return prescriptionOnlyProducts;
    if (selectedCategory === 'Over the Counter') return otcProducts;
    if (selectedCategory === 'Others') return others;
    return [];
  }, [
    selectedCategory,
    frequentlyBought,
    prescriptionOnlyProducts,
    otcProducts,
    others,
  ]);

  // Shows fallback skeletons if loading the initial array or explicitly transitioning pages
  const showSkeletonLoader = productsLoading && products.length === 0;

  return (
    <div className="min-h-screen flex flex-col">
      <Header showSearchInHeader={!isSearchBarVisible} />

      {isBranchMissing ? (
        <main className="flex-1 bg-white flex items-center justify-center">
          <div className="text-center space-y-1.5">
            <h1 className="text-base font-semibold">No branch resolved</h1>
            <p className="text-sm text-muted-foreground">
              Ensure you are visiting your custom URL under famasi.online or
              pass a custom_url query param during local development.
            </p>
          </div>
        </main>
      ) : (
        <>
          <Hero isLoading={branchLoading || channelLoading} />

          <main className="flex-1 bg-white">
            <div
              ref={searchBarRef}
              className="sticky top-12 border-b border-border bg-white shadow-xs z-10"
            >
              <div className="container mx-auto px-6 md:px-16 lg:px-24">
                <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-2 py-2">
                  <SegmentedControl
                    value={activeTab}
                    onValueChange={handleTabChange}
                    className="w-auto"
                  >
                    <SegmentedControlItem
                      value="all"
                      className="text-xs"
                      badge={
                        <Badge
                          variant="secondary"
                          className="ml-1.5 rounded-full text-[10px] px-1.5 py-0"
                        >
                          {allCount}
                        </Badge>
                      }
                    >
                      <span className="hidden md:inline">All Products</span>
                      <span className="inline md:hidden">All</span>
                    </SegmentedControlItem>
                    <SegmentedControlItem
                      value="prescription"
                      className="text-xs"
                      badge={
                        <Badge
                          variant="secondary"
                          className="ml-1.5 rounded-full text-[10px] px-1.5 py-0"
                        >
                          {prescriptionCount}
                        </Badge>
                      }
                    >
                      <span className="hidden md:inline">
                        Prescription Only
                      </span>
                      <span className="inline md:hidden">Rx only</span>
                    </SegmentedControlItem>
                    <SegmentedControlItem
                      value="otc"
                      className="text-xs"
                      badge={
                        <Badge
                          variant="secondary"
                          className="ml-1.5 rounded-full text-[10px] px-1.5 py-0"
                        >
                          {otcCount}
                        </Badge>
                      }
                    >
                      <span className="hidden md:inline">Over the Counter</span>
                      <span className="inline md:hidden">OTC</span>
                    </SegmentedControlItem>
                  </SegmentedControl>
                  <div
                    className="w-full md:w-auto transition-opacity duration-200 ease-out-cubic"
                    style={{ opacity: isSearchBarVisible ? 1 : 0 }}
                  >
                    <SearchCommand />
                  </div>
                </div>
              </div>
            </div>

            <section className="pt-6 pb-10 md:pb-16 bg-[var(--dispensary-white)]">
              <div className="container mx-auto">
                {productsError ? (
                  <Alert
                    variant="destructive"
                    className="mb-4 mx-6 md:mx-16 lg:mx-24"
                  >
                    <HugeiconsIcon icon={AlertCircleIcon} size={14} />
                    <AlertTitle className="text-sm">Error</AlertTitle>
                    <AlertDescription className="text-xs">
                      There was a problem loading the products. Please try again
                      later.
                    </AlertDescription>
                  </Alert>
                ) : showSkeletonLoader ? (
                  <div className="space-y-6 px-6 md:px-16 lg:px-24">
                    {Array(2)
                      .fill(null)
                      .map((_, index) => (
                        <div key={index}>
                          <Skeleton className="h-5 w-1/4 mb-3" />
                          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-5">
                            {Array(4)
                              .fill(null)
                              .map((_, i) => (
                                <ProductCardSkeleton key={i} />
                              ))}
                          </div>
                        </div>
                      ))}
                  </div>
                ) : activeTab === 'prescription' || activeTab === 'otc' ? (
                  <div className="px-6 md:px-16 lg:px-24">
                    <ProductGrid
                      products={activeProducts}
                      isLoading={productsLoading}
                    />
                  </div>
                ) : selectedCategory ? (
                  <div className="px-6 md:px-16 lg:px-24">
                    <div className="flex items-center justify-between mb-3">
                      <h2 className="text-base md:text-lg tracking-tight">
                        {selectedCategory}
                      </h2>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedCategory(null)}
                        className="bg-white hover:ring-2 hover:ring-primary text-xs h-7 px-2"
                      >
                        <HugeiconsIcon
                          icon={ArrowLeft01Icon}
                          size={12}
                          className="mr-1"
                        />
                        Back to all categories
                      </Button>
                    </div>
                    <ProductGrid
                      products={productsForCategoryView}
                      isLoading={productsLoading}
                    />
                  </div>
                ) : isProductsRoute ? (
                  <div className="px-6 md:px-16 lg:px-24">
                    <div className="flex items-center justify-between mb-3">
                      <h2 className="text-base md:text-lg tracking-tight">
                        All Products
                      </h2>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate('/')}
                        className="bg-white hover:ring-2 hover:ring-primary text-xs h-7 px-2"
                      >
                        <HugeiconsIcon
                          icon={ArrowLeft01Icon}
                          size={12}
                          className="mr-1"
                        />
                        Back to sections
                      </Button>
                    </div>
                    <ProductGrid
                      products={products}
                      isLoading={productsLoading}
                    />
                  </div>
                ) : (
                  <div className="px-6 md:px-16 lg:px-24">
                    <ProductCategoryRow
                      title="Frequently Bought"
                      products={frequentlyBought}
                      limit={8}
                      totalCount={frequentlyBoughtCount}
                      onViewAll={() => {
                        setSelectedCategory('Frequently Bought');
                      }}
                    />
                    {prescriptionOnlyProducts.length > 0 && (
                      <ProductCategoryRow
                        title="Prescription Only"
                        products={prescriptionOnlyProducts}
                        totalCount={prescriptionCount}
                        onViewAll={() =>
                          setSelectedCategory('Prescription Only')
                        }
                      />
                    )}
                    {otcProducts.length > 0 && (
                      <ProductCategoryRow
                        title="Over the Counter"
                        products={otcProducts}
                        totalCount={otcCount}
                        onViewAll={() =>
                          setSelectedCategory('Over the Counter')
                        }
                      />
                    )}
                    {others.length > 0 && (
                      <ProductCategoryRow
                        title="Others"
                        products={others}
                        totalCount={othersCount}
                        onViewAll={() => setSelectedCategory('Others')}
                      />
                    )}

                    {products.length > 0 && (
                      <div className="flex justify-center mt-8 mb-4">
                        <Button
                          variant="outline"
                          className="w-full sm:w-auto border-primary text-primary hover:bg-primary/5 px-8"
                          onClick={() => navigate('/products')}
                        >
                          View All ({allCount}) Products
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {/* Pagination Controls */}
                {!isSectionView && pagination && pagination.totalPages > 1 && (
                  <div className="flex items-center justify-center gap-4 mt-12 px-6 md:px-16 lg:px-24">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={pagination.page === 1 || productsLoading}
                      className="text-xs min-w-[80px]"
                    >
                      Previous
                    </Button>

                    <span className="text-xs text-muted-foreground font-medium">
                      Page {pagination.page} of {pagination.totalPages}
                    </span>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={pagination.page === pagination.totalPages || productsLoading}
                      className="text-xs min-w-[80px]"
                    >
                      Next
                    </Button>
                  </div>
                )}
              </div>
            </section>
          </main>
        </>
      )}

      <Footer />
      <ProductDetailSheet />
    </div>
  );
};

export default Index;